/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useMemo } from 'react';

import classnames from 'classnames';

import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import EntityName from 'components/Globals/EntityName';
import { usePaywallContext } from 'components/Paywall';
import Actions from 'components/Productions/Display/Slugs/ProductionPublicSlug/Actions/Actions';
import WorkTypeTag from 'components/Work/Display/WorkTypeTag';

import { getProductionCredits, getProductionWorkTypeTag } from 'utils/productions';

import { ENTITY_TYPES } from 'operabase-router/constants';

import classes from './CalendarSlug.module.scss';

const WorkTag = ({ production }) => {
  const workTypeTag = getProductionWorkTypeTag(production);

  return <WorkTypeTag tag={workTypeTag} className={classes.workTag} />;
};

const ProductionName = ({ production, trackingData }) => {
  const { name, productionWorks } = production || {};
  const work = productionWorks?.[0];

  return (
    <div className={classes.productionName}>
      {name && (
        <Typography className={classes.productionName__name} size={12} weight="medium">
          {name}
        </Typography>
      )}
      {!name && (
        <Typography size={12} className={classes.productionName__workName} key={work?.work?.id}>
          <EntityName entityType={ENTITY_TYPES.WORK} entity={work?.work} trackingData={trackingData} withComposers />
        </Typography>
      )}
    </div>
  );
};

const Location = ({ venues, onExpand, icon, isArchived, cityCountry }) => {
  const { setIsPaywallExpanded } = usePaywallContext();
  const venue = venues?.[0];

  const cityText = cityCountry?.cityText ?? (venue?.city?.name || '');
  const countryText = cityCountry?.countryText ?? (venue?.country?.name || '');

  return (
    <div
      className={classes.locationSingle}
      {...(isArchived && {
        onClick: e => {
          e.stopPropagation();
          e.preventDefault();
          setIsPaywallExpanded(true);
        },
      })}
    >
      <span className={classes.expandIcon}>
        {venues?.length > 1 && <SpriteIcon icon={icon} size={14} onClick={onExpand} />}
      </span>
      <div className={classes.venues}>
        <span className={classes.venues__location}>
          {cityText && (
            <Typography size={12} weight="medium" blur={isArchived}>
              {cityText}
            </Typography>
          )}
          {countryText && (
            <Typography size={12} color="secondary" blur={isArchived}>
              {countryText}
            </Typography>
          )}
        </span>
      </div>
    </div>
  );
};

const ProducerName = ({ producer, trackingData, isArchived }) => (
  <Typography size={12} color="secondary" blur={isArchived}>
    <EntityName
      entityType={producer?.entityType}
      entity={{ id: producer?.entity?.id, name: producer?.entity?.name }}
      trackingData={trackingData}
      isRaw
    />
  </Typography>
);

const CalendarSlug = ({
  production,
  entity,
  entityType,
  trackingData,
  handleExpandSlug,
  isExpanded,
  isProView,
  cityCountry,
  isCastingToolPage,
  styles,
}) => {
  const { setIsPaywallExpanded } = usePaywallContext();

  const productionCredits = useMemo(
    () =>
      getProductionCredits(production, {
        entityId: entity?.id,
        entityType,
      }),
    [entity?.id, entityType, production],
  );

  const { directors, conductors, producers } = productionCredits || {};
  const producer = producers?.data?.[0];
  const entityContribution =
    production?.contributions?.find(contribution => contribution?.profile?.id === entity?.id)?.credit || null;

  const isArchived = production?.isArchived;

  const icon = useMemo(() => {
    if (isExpanded) {
      return 'keyboard_arrow_up';
    }

    return 'expand_more';
  }, [isExpanded]);

  return (
    <div className={classnames(classes.calendarSlug, { [styles?.calendarSlug]: !!styles?.calendarSlug })}>
      <div className={classes.divider} />
      <div
        className={classnames(classes.calendarSlug__leftBlock, {
          [classes.calendarSlug__leftBlockCTPage]: isCastingToolPage,
        })}
      >
        <div className={classes.calendarSlug__leftBlock_genre}>
          <WorkTag production={production} className={classes.workTag} />
        </div>
        <div className={classes.calendarSlug__leftBlock_prodName}>
          <ProductionName production={production} trackingData={trackingData} />
          <div className={classes.tabletProducer}>
            <ProducerName producer={producer} trackingData={trackingData} isArchived={isArchived} />
          </div>
        </div>
        <div
          className={classes.calendarSlug__leftBlock_producer}
          {...(isArchived && {
            onClick: e => {
              e.stopPropagation();
              e.preventDefault();
              setIsPaywallExpanded(true);
            },
          })}
        >
          <ProducerName producer={producer} trackingData={trackingData} isArchived={isArchived} />
        </div>
        <div className={classes.calendarSlug__leftBlock_conductorDirector}>
          {directors?.data?.[0]?.entity?.id && (
            <div className={classes.directors}>
              <Typography size="12" color="secondary" className={classes.title}>
                D:
              </Typography>
              <Typography size={12} color="secondary" truncate className={classes.name}>
                <EntityName
                  key={directors?.data?.[0]?.entity?.id}
                  entityType={ENTITY_TYPES.PROFILE}
                  entity={directors?.data?.[0]?.entity}
                  trackingData={trackingData}
                />
              </Typography>
            </div>
          )}
          {conductors?.data?.[0]?.entity?.id && (
            <div className={classes.conductors}>
              <Typography size="12" color="secondary" className={classes.title}>
                C:
              </Typography>
              <Typography size={12} color="secondary" truncate className={classes.name}>
                <EntityName
                  key={conductors?.data?.[0]?.entity?.id}
                  entityType={ENTITY_TYPES.PROFILE}
                  entity={conductors?.data?.[0]?.entity}
                  trackingData={trackingData}
                />
              </Typography>
            </div>
          )}
        </div>
        <div className={classes.calendarSlug__leftBlock_countryCity}>
          <Location
            venues={production?.venues}
            icon={icon}
            onExpand={handleExpandSlug}
            isArchived={isArchived}
            cityCountry={cityCountry}
          />
          <div className={classes.tabletTicketAction}>
            <Actions
              production={production}
              entity={entity}
              entityType={entityType}
              variant="textAndBlock"
              trackingData={trackingData}
            />
          </div>
        </div>
        <div className={classes.calendarSlug__leftBlock_ticket}>
          {isProView ? (
            <Typography size={12} weight="bold" className={classes.entityContribution}>
              {entityContribution}
            </Typography>
          ) : (
            <Actions
              data={production}
              entity={entity}
              entityType={entityType}
              variant="textAndBlock"
              trackingData={trackingData}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendarSlug;
